import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import useThrottle from 'pathfinder/utils/useThrottle';
import { useWikiRecommendationsContext } from '../helpers/WikiRecommendationsContext';
import ArrowButton from './ArrowButton';
import CarouselItem from './CarouselItem';
import throttle from 'lodash/throttle';
import Dots from './Dots';
import { useOnScreenCallback } from 'pathfinder/modules/shared/useOnScreen';
import { getReadableLabel } from 'pathfinder/utils/getReadableLabel';
import { getArticleId } from 'pathfinder/modules/OnWikiRecommendations/helpers/getIds';
import styles from '../styles/RecommendationsBox.module.scss';
const MAX_RECOMMENDATIONS_NUMBER = 9;
export const RecommendationsBox = () => {
    const { wikiId, articleId } = getArticleId();
    const { recommendations, track, isRightRail } = useWikiRecommendationsContext();
    const itemsWrapperRef = useRef();
    const [onScreen, ref] = useOnScreenCallback();
    const [impressionTracked, setImpressionTracked] = useState(false);
    const [sliderData, setSliderData] = React.useState({});
    const [size, setSize] = React.useState({ width: window.innerWidth, height: window.innerHeight });
    const [scrollLeft, setScrollLeft] = React.useState(0);
    const throttleScroll = useThrottle((scrollLeft) => {
        setScrollLeft(scrollLeft);
    }, 200);
    const placement = isRightRail ? 'right-rail' : 'in-content';
    const renderTimestamp = new Date().getTime();
    const isFirstPage = () => {
        return sliderData.currentPage === 0;
    };
    const isLastPage = () => {
        return sliderData.currentPage === sliderData.pages - 1;
    };
    const canScrollRight = () => {
        const { pages } = sliderData;
        const page = sliderData.currentPage;
        return page < pages - 1;
    };
    const canScrollLeft = () => {
        const { isAtTheBeginningOfThePage } = sliderData;
        const page = sliderData.currentPage;
        return page > 0 && isAtTheBeginningOfThePage;
    };
    const trackSliderScroll = useThrottle((pageNumber, leftScroll) => {
        if (!isNaN(pageNumber) && leftScroll !== 0) {
            track({
                action: 'scroll-slider',
                label: `scroll-to-page-${pageNumber} ${placement}`,
            });
        }
    }, 500, false, true);
    const scrollToPage = (page) => {
        const itemsWrapper = itemsWrapperRef.current;
        itemsWrapper.scrollTo({
            left: page * sliderData.pageOffset,
            behavior: 'smooth',
        });
    };
    const scrollItemsWrapper = (isScrollRight) => {
        let page = sliderData.currentPage;
        if (isScrollRight && canScrollRight()) {
            page++;
            scrollToPage(page);
        }
        else if (!isScrollRight && canScrollLeft()) {
            page--;
            scrollToPage(page);
        }
    };
    useEffect(() => {
        const itemsWrapper = itemsWrapperRef.current;
        const item = itemsWrapper.querySelector(':scope > a');
        const gapSize = parseInt(getComputedStyle(item).marginRight, 10);
        const itemWidth = item.offsetWidth + gapSize;
        const pageOffset = Math.floor((itemsWrapper.offsetWidth + gapSize) / itemWidth) * itemWidth;
        const pages = Math.ceil(itemsWrapper.scrollWidth / pageOffset);
        const isAtTheEndOfTheSlider = scrollLeft + itemsWrapper.offsetWidth === itemsWrapper.scrollWidth;
        const isAtTheBeginningOfThePage = scrollLeft % pageOffset === 0 || isAtTheEndOfTheSlider;
        let currentPage = Math.floor(scrollLeft / pageOffset);
        if (isAtTheEndOfTheSlider) {
            currentPage = pages - 1;
        }
        setSliderData({ pageOffset, pages, currentPage, isAtTheBeginningOfThePage });
    }, [size, scrollLeft]);
    useEffect(() => {
        var _a, _b;
        if (onScreen && !impressionTracked) {
            const scrollDepth = Math.round(document.documentElement.scrollTop + ((_b = (_a = itemsWrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().top) !== null && _b !== void 0 ? _b : 0));
            const timestamp = new Date().getTime() - renderTimestamp;
            track.impression({
                label: getReadableLabel({ wikiId, articleId }, `placement: ${placement} | scrollDepth: ${scrollDepth}px | timestamp: ${timestamp}ms`),
            });
            setImpressionTracked(true);
        }
    }, [onScreen]);
    useEffect(() => {
        trackSliderScroll(sliderData.currentPage + 1, scrollLeft);
    }, [sliderData.currentPage]);
    useEffect(() => {
        const throttledResizeHandler = throttle(() => {
            setSize({ width: window.innerWidth, height: window.innerHeight });
        }, 200);
        window.addEventListener('resize', throttledResizeHandler);
        return () => {
            window.removeEventListener('resize', throttledResizeHandler);
        };
    }, []);
    return (_jsxs("div", Object.assign({ className: styles.wrapper, ref: ref }, { children: [_jsx("div", Object.assign({ className: styles.header }, { children: "Others like you also viewed" }), void 0), _jsx("div", Object.assign({ className: `related-content-items-wrapper ${styles.itemsWrapper}`, ref: itemsWrapperRef, onScroll: () => {
                    var _a;
                    throttleScroll((_a = itemsWrapperRef.current) === null || _a === void 0 ? void 0 : _a.scrollLeft);
                } }, { children: recommendations
                    .map((recommendation) => (_jsx(CarouselItem, { src: recommendation.thumbnail_url, title: recommendation.article_title, link: recommendation.url, wiki: recommendation.wiki_title, id: recommendation.item_id }, recommendation.item_id)))
                    .slice(0, MAX_RECOMMENDATIONS_NUMBER) }), void 0), _jsx(ArrowButton, { position: "left", isActive: !isFirstPage(), onClick: () => scrollItemsWrapper(false), name: "scrollLeftButton", ariaLabel: "Previous slide" }, void 0), _jsx(ArrowButton, { position: "right", isActive: !isLastPage(), onClick: () => scrollItemsWrapper(true), name: "scrollRightButton", ariaLabel: "Next slide" }, void 0), _jsx(Dots, { pages: sliderData.pages, page: sliderData.currentPage, onDotClick: (page) => {
                    scrollToPage(page);
                } }, void 0)] }), void 0));
};
